import { FC, useEffect } from "react";
import styled from "styled-components";
import { Button } from "src/components/ui";
import { useLazyGetOccupationAnalyticsQuery } from "src/store/services/miscApi";
import { toast } from "react-toastify";

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 16px;
	width: 100%;
	max-width: 700px;
	max-height: 90vh;
	overflow-y: auto;
	padding: 24px;
`;

const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;

	h2 {
		font-size: 20px;
		font-weight: 600;
		color: ${(props) => props.theme.colors.text_01};
	}

	.actions {
		display: flex;
		gap: 16px;
		align-items: center;
	}
`;

const Section = styled.div`
	margin-bottom: 24px;

	h3 {
		font-size: 16px;
		font-weight: 600;
		color: ${(props) => props.theme.colors.text_01};
		margin-bottom: 12px;
	}
`;

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	gap: 16px;
	margin-bottom: 24px;
`;

const StatCard = styled.div`
	background: ${(props) => props.theme.colors.bg_02 || "#f5f5f5"};
	padding: 16px;
	border-radius: 12px;

	.label {
		font-size: 14px;
		color: ${(props) => props.theme.colors.text_02};
		margin-bottom: 8px;
	}

	.value {
		font-size: 24px;
		font-weight: 600;
		color: ${(props) => props.theme.colors.text_01};
	}
`;

const DataRow = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 12px;
	border-bottom: 1px solid ${(props) => props.theme.colors.border || "#e0e0e0"};

	&:last-child {
		border-bottom: none;
	}

	.label {
		font-size: 14px;
		color: ${(props) => props.theme.colors.text_02};
	}

	.value {
		font-size: 14px;
		font-weight: 500;
		color: ${(props) => props.theme.colors.text_01};
	}
`;

const CountryList = styled.div`
	background: ${(props) => props.theme.colors.bg_02 || "#f5f5f5"};
	padding: 16px;
	border-radius: 12px;
`;

const CloseButton = styled(Button)`
	background: ${(props) => props.theme.colors.purple};
	color: white;
	padding: 8px 24px;
	border-radius: 8px;

	&:hover {
		opacity: 0.9;
	}
`;

const LoadingContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 40px;
	color: ${(props) => props.theme.colors.text_02};
`;

interface Props {
	occupationName: string;
	onClose: () => void;
}

export const OccupationDetailsModal: FC<Props> = ({
	occupationName,
	onClose,
}) => {
	const [getOccupationAnalytics, { data, isLoading, isError }] =
		useLazyGetOccupationAnalyticsQuery();

	useEffect(() => {
		if (occupationName) {
			getOccupationAnalytics(occupationName);
		}
	}, [occupationName, getOccupationAnalytics]);

	useEffect(() => {
		if (isError) {
			toast.error("Failed to load occupation analytics");
		}
	}, [isError]);

	const analytics = data?.payload?.data;

	return (
		<ModalOverlay onClick={onClose}>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<h2>Occupation Analytics</h2>
					<div className='actions'>
						<svg
							width='24'
							height='24'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							style={{ cursor: "pointer" }}
							onClick={onClose}
						>
							<path d='M18 6L6 18M6 6l12 12' />
						</svg>
					</div>
				</ModalHeader>

				{isLoading ? (
					<LoadingContainer>Loading analytics...</LoadingContainer>
				) : analytics ? (
					<>
						<Section>
							<h3>{analytics.occupation}</h3>
							<StatsGrid>
								<StatCard>
									<div className='label'>Total Vendors</div>
									<div className='value'>{analytics.totalVendors}</div>
								</StatCard>
							</StatsGrid>
						</Section>

						<Section>
							<h3>Gender Distribution</h3>
							<StatsGrid>
								<StatCard>
									<div className='label'>Male</div>
									<div className='value'>
										{analytics.genderDistribution.male}
									</div>
								</StatCard>
								<StatCard>
									<div className='label'>Female</div>
									<div className='value'>
										{analytics.genderDistribution.female}
									</div>
								</StatCard>
							</StatsGrid>
						</Section>

						<Section>
							<h3>Age Groups</h3>
							<CountryList>
								<DataRow>
									<span className='label'>18–24</span>
									<span className='value'>{analytics.ageGroups["18–24"]}</span>
								</DataRow>
								<DataRow>
									<span className='label'>25–34</span>
									<span className='value'>{analytics.ageGroups["25–34"]}</span>
								</DataRow>
								<DataRow>
									<span className='label'>35–45</span>
									<span className='value'>{analytics.ageGroups["35–45"]}</span>
								</DataRow>
								<DataRow>
									<span className='label'>45+</span>
									<span className='value'>{analytics.ageGroups["45+"]}</span>
								</DataRow>
							</CountryList>
						</Section>

						<Section>
							<h3>Countries</h3>
							<CountryList>
								{analytics.countries.map((country, index) => (
									<DataRow key={index}>
										<span className='label'>{country.country}</span>
										<span className='value'>{country.count}</span>
									</DataRow>
								))}
							</CountryList>
						</Section>

						<div style={{ textAlign: "right" }}>
							<CloseButton onClick={onClose}>Close</CloseButton>
						</div>
					</>
				) : (
					<LoadingContainer>No data available</LoadingContainer>
				)}
			</ModalContent>
		</ModalOverlay>
	);
};
